import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import TextInput from '../../components/TextInput';
import Button from '../../components/Button';
import SkeletonLoader from '../../components/SkeletonLoader';
import userApi from '../../api/userApi';
import { useToast } from '../../contexts/ToastContext';
import RolesMultiSelect from '../../components/RolesMultiSelect';

const EditUser = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const { addToast } = useToast();
  const [loading, setLoading] = useState(true);
  const [generalError, setGeneralError] = useState(null);
  const [validationErrors, setValidationErrors] = useState({});
  const [formData, setFormData] = useState({
    first_name: '',
    last_name: '',
    email: '',
    password: '',
    roles: [],
  });
  const [userFound, setUserFound] = useState(true);

  useEffect(() => {
    const fetchUserAndRoles = async () => {
      setLoading(true);
      setGeneralError(null);
      setValidationErrors({});
      try {
        const userResponse = await userApi.getUserByIdentifier(id);
        if (userResponse.success && userResponse.data) {
          const userToEdit = userResponse.data;
          setFormData(prev => ({
            ...prev,
            first_name: userToEdit.first_name,
            last_name: userToEdit.last_name,
            email: userToEdit.email,
            roles: userToEdit.roles.map(role => role.id.toString()),
          }));
          setUserFound(true);
        } else {
          setUserFound(false);
          setGeneralError(userResponse.message || 'User not found.');
        }
      } catch (err) {
        console.error('Failed to fetch User or Roles:', err);
        setUserFound(false);
        setGeneralError(err.response?.data?.message || 'Failed to load User or Roles. Please try again.');
      } finally {
        setLoading(false);
      }
    };

    fetchUserAndRoles();
  }, [id, addToast]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData({
      ...formData,
      [name]: value,
    });
    if (validationErrors[name]) {
      setValidationErrors(prev => ({ ...prev, [name]: undefined }));
    }
  };

  const handleRoleChange = (selectedOptions) => {
    const selectedRoleIds = selectedOptions ? selectedOptions.map(option => option.value) : [];
    setFormData(prev => ({
      ...prev,
      roles: selectedRoleIds,
    }));
    if (validationErrors.roles) {
      setValidationErrors(prev => ({ ...prev, roles: undefined }));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setGeneralError(null);
    setValidationErrors({});

    const data = new FormData();
    for (const key in formData) {
      if (key === 'roles') {
        formData.roles.forEach(roleId => data.append('roles[]', parseInt(roleId, 10)));
      } else if (key === 'password' && !formData[key]) {
        continue;
      } else {
        data.append(key, formData[key]);
      }
    }
    data.append('_method', 'PUT');

    try {
      await userApi.updateUser(id, data);
      addToast('User updated successfully!', 'success');
      navigate('/users');
    } catch (err) {
      console.error('Failed to update User:', err);
      if (err.response && err.response.status === 422) {
        setValidationErrors(err.response.data.errors || {});
        addToast(err.response.data.message || 'Validation failed. Please check your input.', 'error');
      } else {
        addToast(err.response?.data?.message || 'Failed to update User. Please try again.', 'error');
      }
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="p-6 bg-gray-800 rounded-lg shadow-md text-text-white text-center">
        <SkeletonLoader type="form" />
      </div>
    );
  }

  if (!userFound) {
    return (
      <div className="p-6 bg-gray-800 rounded-lg shadow-md text-text-white text-center">
        <h1 className="text-3xl font-bold mb-4">User Not Found</h1>
        <p className="text-text-gray">{generalError || 'The User you are trying to edit does not exist.'}</p>
        <Button
          onClick={() => navigate('/users')}
          className="mt-4 bg-gray-500 hover:bg-gray-600 text-white"
        >
          Go to Users List
        </Button>
      </div>
    );
  }

  return (
    <div className="p-6 bg-gray-800 rounded-lg shadow-md text-text-white">
      <h1 className="text-3xl font-bold mb-6">Edit User</h1>
      {generalError && <div className="text-red-500 mb-4">{generalError}</div>}
      <form onSubmit={handleSubmit}>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <TextInput
            label="First Name"
            name="first_name"
            id="first_name"
            value={formData.first_name}
            onChange={handleChange}
            required={true}
            placeholder="First Name"
            error={validationErrors?.first_name?.[0]}
          />
          <TextInput
            label="Last Name"
            name="last_name"
            id="last_name"
            value={formData.last_name}
            onChange={handleChange}
            required={true}
            placeholder="Last Name"
            error={validationErrors?.last_name?.[0]}
          />
          <TextInput
            label="Email"
            name="email"
            id="email"
            type="email"
            value={formData.email}
            onChange={handleChange}
            required={true}
            placeholder="Email"
            error={validationErrors?.email?.[0]}
          />
          <RolesMultiSelect
            label="Roles"
            value={formData.roles}
            onChange={handleRoleChange}
            error={validationErrors?.roles?.[0]}
            required={true}
          />
          <TextInput
            label="Password (leave blank to keep current)"
            name="password"
            id="password"
            type="password"
            value={formData.password}
            onChange={handleChange}
            placeholder="New Password"
            error={validationErrors?.password?.[0]}
          />
        </div>
        <div className="flex items-center justify-between mt-6">
          <Button
            type="submit"
            className="bg-accent-red hover:bg-red-700 text-white"
            loading={loading}
          >
            Update User
          </Button>
          <Button
            type="button"
            onClick={() => navigate('/users')}
            className="bg-gray-500 hover:bg-gray-600 text-white"
            disabled={loading}
          >
            Cancel
          </Button>
        </div>
      </form>
    </div>
  );
};

export default EditUser;